import { useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Image } from '@/shared/ui/image';
import { Button } from '@/shared/ui/form/button';
import {
  Content,
  Root,
  ContentHeader,
  Item,
  Status,
  List,
  UnreadDot,
  NotifyAvatar,
  BoxTime,
} from './styles';
import {
  BodyTextMediumB,
  BodyTextRegularB,
  BodyTextMediumA,
  BodyTextRegularC,
} from '@/shared/ui/typography2';
import { Box } from '@/shared/ui/box';
import { Avatar } from '@/shared/ui/avatar';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useRouter } from 'next-nprogress-bar';
import { Badge } from '@/shared/ui/badge/badge';
import {
  useNotifyList,
  useNotificationDate,
} from '@/features/notify-managment/model/hooks';
import { useI18n } from '@locales/client';
import { Condition } from '@/shared/ui/condition';
import { useLocaleFormat } from '@/shared/hooks';
import { timestampToDate } from '@/features/wallet-managment/model/utils/groupTransactionsByDate';

const NotifyManagment = ({ mobileLandscape }) => {
  const { user } = useUser();
  const {
    notifyList,
    isNotifyListLoading,
    markReadAllTrigger,
    notifyListMutate,
  } = useNotifyList();

  const router = useRouter();
  const t = useI18n();
  const formatUrl = useLocaleFormat();

  const { groupedNotifications, formatGroupHeader, formatNotificationTime } =
    useNotificationDate(notifyList);

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  if (isNotifyListLoading) {
    return null;
  }

  const newNotifyList = Array.isArray(notifyList)
    ? notifyList.filter((item) => !item?.read)
    : [];

  const markReadAll = async (isOpened) => {
    if (mobileLandscape && isOpened) {
      document.body.style.overflow = 'hidden';
    }

    if (mobileLandscape && !isOpened) {
      document.body.style.overflow = 'auto';
    }

    if (isOpened && newNotifyList?.length) {
      await markReadAllTrigger();
    }
    if (!isOpened && newNotifyList?.length) {
      await notifyListMutate();
    }
  };

  const renderNotificationContent = (item) => {
    return {
      title: item?.title,
      text: item?.text,
      icon: item?.type,
    };
  };

  return (
    <Root onOpenChange={(bool) => markReadAll(bool)}>
      <Popover.Trigger asChild>
        <Box $display={'flex'} $gap={mobileLandscape ? 0 : 12}>
          <Box $position="relative" $flex="auto">
            <Button
              type="button"
              variant="gray"
              style={mobileLandscape ? { width: '40px', height: '40px' } : null}
              icon={
                <Image
                  style={{ display: 'flex' }}
                  width={20}
                  height={20}
                  src="/images/icons/bell-icon.svg"
                  alt="bell-icon"
                />
              }
            />
            <Condition match={newNotifyList?.length > 0}>
              <Badge color="#dd3636" text={newNotifyList?.length} />
            </Condition>
          </Box>

          <NotifyAvatar onClick={() => router.push('/profile')}>
            <Avatar
              avatar={user?.avatar}
              router={router}
              mobileLandscape={mobileLandscape}
            />
          </NotifyAvatar>
        </Box>
      </Popover.Trigger>

      <Content align="end" sideOffset={8}>
        <ContentHeader>
          <Box $display="flex" $gap={10}>
            <BodyTextMediumA>{t('notifications.title')}</BodyTextMediumA>
          </Box>
          <Popover.Close
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <Image
              src="/images/icons/close-dialog-secondary.svg"
              width={20}
              height={20}
              alt="close-dialog-secondary"
            />
          </Popover.Close>
        </ContentHeader>
        <Condition match={groupedNotifications?.length > 0}>
          <List>
            {groupedNotifications?.map((group) => (
              <Box key={group?.date?.toISOString()}>
                <Box $textAlign="center" $marginBottom="8px">
                  <BodyTextRegularB color="#AFAFAF">
                    {formatGroupHeader(group?.date)}
                  </BodyTextRegularB>
                </Box>

                <Condition match={group?.items?.length > 0}>
                  <Box $display="flex" $flexDirection="column" $gap={8}>
                    {group?.items?.map((item) => {
                      const content = renderNotificationContent(item);

                      return (
                        <Item
                          key={item?.ts}
                          $status={item?.status}
                          href={item?.link ? formatUrl(item?.link) : '#'}
                        >
                          <Box
                            $display="flex"
                            $alignItems="flex-start"
                            $gap="12px"
                          >
                            <Status $status={item?.status}>
                              <Condition match={!item?.read}>
                                <UnreadDot />
                              </Condition>
                              <Box $padding="8px">
                                <Image
                                  src={`/images/icons/toast/${content?.icon}.svg`}
                                  width={24}
                                  height={24}
                                />
                              </Box>
                            </Status>
                            <Box
                              $display="flex"
                              $flexDirection="column"
                              $gap={6}
                            >
                              <Box $maxWidth="220px" $overflow="hidden">
                                <BodyTextMediumB>
                                  {content?.title}
                                </BodyTextMediumB>
                              </Box>
                              <Box
                                $display="flex"
                                $flexDirection="column"
                                $gap={12}
                              >
                                <BodyTextRegularB>
                                  {content.text}
                                </BodyTextRegularB>
                                <Condition match={Boolean(item?.image)}>
                                  <Image
                                    src={item?.image}
                                    style={{ borderRadius: '8px' }}
                                    width="100%"
                                    height={mobileLandscape ? 130 : 170}
                                    alt={item?.image}
                                  />
                                </Condition>

                                <Condition match={Boolean(item?.action)}>
                                  <Button
                                    variant="orangeGradient"
                                    type="button"
                                  >
                                    <BodyTextMediumB>
                                      {item?.action}
                                    </BodyTextMediumB>
                                  </Button>
                                </Condition>
                              </Box>
                            </Box>

                            <BoxTime>
                              <BodyTextRegularC color="#AFAFAF">
                                {formatNotificationTime(
                                  timestampToDate(item?.ts)
                                )}
                              </BodyTextRegularC>
                            </BoxTime>
                          </Box>
                        </Item>
                      );
                    })}
                  </Box>
                </Condition>
              </Box>
            ))}
          </List>
        </Condition>
      </Content>
    </Root>
  );
};

export { NotifyManagment };
